export type PricingMode = "SELF_SERVE" | "CONCIERGE";
export type PayerType = "" | "MEDICAID" | "MEDICARE" | "PRIVATE" | "VA" | "UNKNOWN";

export const PAYER_OPTIONS: { label: string; value: PayerType }[] = [
  { label: "Private Pay", value: "PRIVATE" },
  { label: "Medicaid", value: "MEDICAID" },
  { label: "Medicare", value: "MEDICARE" },
  { label: "VA", value: "VA" },
  { label: "Other / Unknown", value: "UNKNOWN" },
];

export const CARE_NEED_OPTIONS = [
  "Memory Care",
  "Dementia",
  "Respite",
  "Hospice",
  "Physical Disability",
  "Mental Health",
];

export const FALLBACK_SOURCE_TYPES = [
  { id: 101, code: "HOSPITAL", name: "Hospital" },
  { id: 102, code: "SOCIAL_WORKER", name: "Social Worker" },
  { id: 103, code: "VA", name: "VA" },
  { id: 104, code: "FAMILY", name: "Family" },
  { id: 105, code: "SELF", name: "Self" },
  { id: 106, code: "OTHER", name: "Other" },
];

export const isValidEmail = (s: string) => /\S+@\S+\.\S+/.test(s.trim());
export const isValidPhone = (s: string) => s.replace(/[^\d]/g, "").length >= 7;

export const toNumId = (v: unknown) =>
  v == null ? null : Number(v) > 0 ? Number(v) : null;

export function stableStringify(obj: any): string {
  const seen = new WeakSet();
  return JSON.stringify(obj, function (_key, value) {
    if (value && typeof value === "object") {
      if (seen.has(value)) return;
      seen.add(value);
      const ordered: Record<string, unknown> = {};
      Object.keys(value)
        .sort()
        .forEach((k) => {
          ordered[k] = (value as any)[k];
        });
      return ordered;
    }
    return value;
  });
}

export const ZIP_RE = /^\d{5}(-\d{4})?$/;
export const isZipValid = (s: string) => ZIP_RE.test(String(s || "").trim());

export const isNoMatchesMessage = (msg: string) =>
  msg === "NO_MATCHES" ||
  /no[_\s-]?matches/i.test(msg) ||
  /No matching providers/i.test(msg);

export const isAuthRequiredMessage = (msg: string) =>
  /AUTH_REQUIRED/i.test(msg) ||
  /Please log in before sending broadcasts/i.test(msg) ||
  /not authenticated/i.test(msg);

export const getFirstApolloMessage = (e: any): string =>
  e?.graphQLErrors?.[0]?.message ||
  e?.message ||
  (typeof e === "string" ? e : "");

const ALPHABET = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
const pick = (n: number) =>
  Array.from({ length: n }, () => ALPHABET[Math.floor(Math.random() * ALPHABET.length)]).join("");

export const generatePatientKey = () => `REF-${pick(4)}-${pick(4)}`;
export const KEY_RE = /^REF-[A-HJ-NP-Z2-9]{4}-[A-HJ-NP-Z2-9]{4}$/i;

export const normalizeResidentKey = (k: string) =>
  (k || "").toUpperCase().replace(/[^A-Z0-9]/g, "");

export type UrgencyChoice = "ASAP" | "WEEK" | "MONTH" | "DATE" | "UNKNOWN";

export function computeUrgency(choice: UrgencyChoice, dateISO?: string) {
  if (choice === "DATE" && dateISO) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const target = new Date(`${dateISO}T00:00:00`);
    if (!Number.isNaN(target.getTime())) {
      const diff = Math.round((target.getTime() - today.getTime()) / 86400000);
      if (diff <= 3) return "IMMEDIATE";
      if (diff <= 7) return "WEEK";
      if (diff <= 30) return "MONTH";
      return "UNKNOWN";
    }
  }
  if (choice === "ASAP") return "IMMEDIATE";
  if (choice === "WEEK") return "WEEK";
  if (choice === "MONTH") return "MONTH";
  return "UNKNOWN";
}

export type AmenityFlags = {
  isPrivateRoom: boolean;
  hasPrivateBathroom: boolean;
  hasHospitalBed: boolean;
  hasHoyerLift: boolean;
  hasOxygen: boolean;
  accommodatesBariatric: boolean;
};

export const buildAmenitiesInput = (f: AmenityFlags) => {
  const out: Partial<AmenityFlags> = {};
  if (f.isPrivateRoom) out.isPrivateRoom = true;
  if (f.hasPrivateBathroom) out.hasPrivateBathroom = true;
  if (f.hasHospitalBed) out.hasHospitalBed = true;
  if (f.hasHoyerLift) out.hasHoyerLift = true;
  if (f.hasOxygen) out.hasOxygen = true;
  if (f.accommodatesBariatric) out.accommodatesBariatric = true;
  return Object.keys(out).length ? out : undefined;
};

export const AMENITY_LABELS: Record<string, string> = {
  isPrivateRoom: "Private room",
  hasPrivateBathroom: "Private bathroom",
  hasHospitalBed: "Hospital bed",
  hasHoyerLift: "Hoyer lift",
  hasOxygen: "Oxygen",
  accommodatesBariatric: "Bariatric friendly",
};

export function summarizeList(items: string[], max = 3) {
  if (!items.length) return "";
  if (items.length <= max) return items.join(", ");
  const head = items.slice(0, max).join(", ");
  return `${head} +${items.length - max} more`;
}
